import { FC, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useMutation } from "@tanstack/react-query";

import { CreateUpdateCompanyAddressV2ResponseType } from "api/response";
import { addCompanyAddress, deleteCompanyAddress, updateCompanyAddress } from "api/services/company";
import { AddressV2, AddressV2FormValues } from "api/models";
import Button from "components/atoms/Button/Button";
import errorToast from "helpers/errorToast";
import Alert from "components/molecules/Alert/Alert";
import Dialog from "../Dialog/Dialog";
import AddressV2Form from "./components/AddressV2Form/AddressV2Form";

const defaultFormValues: AddressV2FormValues = {
  city_id: null,
  city_name: "",
  custom_city_name: null,
  country_id: null,
  country_name: "",
  state_id: null,
  state_name: "",
  locode_id: null,
  un_locode: "",

  name: "",
  address_label: "none",
  street_address: "",
  street_address_line_two: "",

  zipcode: "",
  eori: "",
  timezone: null,
  location_reference_number: "",
  custom_address_reference: "",
  address_within_airport_or_port: false,
  location_reference_number_enabled: false,
  custom_address_reference_enabled: false,

  preferred_contact: null,
  selected_shipper_location: null,
};

type AddressBookDialogProps = {
  /**
   * Whether the dialog is open.
   */
  isOpen: boolean;
  /**
   * A function to close the dialog.
   */
  onClose: () => void;
  /**
   * The address ID to be used to populate the dialog.
   */
  selectedAddress?: AddressV2;
  /**
   * Refetch addresses
   */
  refetchAddresses: () => void;
  /**
   * Source from where the form is being called
   */
  source: "address_book" | "quick_quote_request";
  /**
   * callback on newly added address
   */
  onAddNewAddress?: (data: CreateUpdateCompanyAddressV2ResponseType) => void;
  /**
   * force address to create a new one
   * even if selected address is passed
   */
  forceCreate?: boolean;
  /**
   * Selected form shipper location
   */
  formSelectedShipperLocation?: string | null;
};

const AddressBookDialog: FC<AddressBookDialogProps> = function (props) {
  const {
    isOpen,
    onClose,
    selectedAddress,
    refetchAddresses,
    source,
    onAddNewAddress,
    forceCreate,
    formSelectedShipperLocation,
  } = props;

  const intl = useIntl();
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const methods = useForm<AddressV2FormValues>({
    defaultValues: defaultFormValues,
  });
  const { reset, handleSubmit } = methods;

  useEffect(() => {
    if (selectedAddress) {
      let preferredContactsValue = null;
      if (selectedAddress.preferred_contact) {
        preferredContactsValue = selectedAddress.preferred_contact ? selectedAddress.preferred_contact.id : null;
      }

      reset({
        ...selectedAddress,
        city_id: Number(selectedAddress.city_id) || null,
        country_id: Number(selectedAddress.country_id) || null,
        state_id: Number(selectedAddress.state_id) || null,
        address_within_airport_or_port: Boolean(selectedAddress.locode_id),
        location_reference_number_enabled: Boolean(selectedAddress.location_reference_number),
        custom_address_reference_enabled: Boolean(selectedAddress.custom_address_reference),
        address_label: selectedAddress.address_label ?? "none",
        preferred_contact: preferredContactsValue,
        selected_shipper_location: selectedAddress?.payment_location_data?.cb_account_number || formSelectedShipperLocation,
      });
    } else {
      reset({ ...defaultFormValues, selected_shipper_location: formSelectedShipperLocation });
    }
    setShowConfirmDelete(false);
  }, [selectedAddress, formSelectedShipperLocation]);

  const { mutate, isLoading: isLoadingMutate } = useMutation({
    mutationFn: (address) =>
      selectedAddress && !forceCreate
        ? updateCompanyAddress(selectedAddress.reference_number, address)
        : addCompanyAddress(address),
    onSuccess: (data) => {
      if (selectedAddress?.reference_number) {
        toast(
          intl.formatMessage({
            id: "settings.address-book.updateAddressSuccess",
            defaultMessage: "Successfully updated address",
          }),
          { type: "success" },
        );
      } else {
        toast(
          intl.formatMessage({
            id: "settings.address-book.addAddressSuccess",
            defaultMessage: "Successfully added new address",
          }),
          { type: "success" },
        );
      }
      if (onAddNewAddress) onAddNewAddress(data.data);
      refetchAddresses();
      onClose();
      reset(defaultFormValues);
    },
    onError: (err) => {
      errorToast(
        err,
        selectedAddress?.reference_number
          ? intl.formatMessage({
              id: "settings.address-book.updateAddressFail",
              defaultMessage: "Fail to update address",
            })
          : intl.formatMessage({
              id: "settings.address-book.addAddressFail",
              defaultMessage: "Fail to add new address",
            }),
      );
    },
  });

  const onSubmit = (data: any) => {
    // Remove unnecessary fields
    const {
      address_within_airport_or_port,
      custom_address_reference_enabled,
      location_reference_number_enabled,

      country_id,
      state_id,
      city_id,

      country_name,
      state_name,
      city_name,
      locode_name,

      custom_city_name,

      location_reference_number,
      custom_address_reference,
      locode_id,

      preferred_contact,

      ...filteredData
    } = data;

    const cityFields = {
      ...(!custom_city_name && typeof city_id === "number" && { city: city_id }),
      ...(custom_city_name && { custom_city_name }),
      ...(city_id && typeof city_id === "string" && { custom_city_name: city_id }),
    };

    const submitData = {
      ...filteredData,
      address_label: data.address_label === "none" ? null : data.address_label,
      country: country_id,
      state: state_id,

      ...cityFields,

      address_source: source,
      locode: address_within_airport_or_port && locode_id ? Number(locode_id) : null,
      location_reference_number:
        location_reference_number_enabled && location_reference_number ? location_reference_number : null,
      custom_address_reference: custom_address_reference_enabled && custom_address_reference ? custom_address_reference : "",
      preferred_contact_id: preferred_contact,
    };

    mutate(submitData);
  };

  const handleDeleteAddress = () => {
    setIsDeleting(true);
    deleteCompanyAddress(selectedAddress?.reference_number!)
      .then(() => {
        toast(
          intl.formatMessage({
            id: "settings.addressBook.deleteSuccess",
            defaultMessage: "Address successfully deleted",
          }),
          {
            type: "success",
          },
        );
        refetchAddresses();
        onClose();
        reset(defaultFormValues);
      })
      .catch((err) => {
        errorToast(err);
      })
      .finally(() => setIsDeleting(false));
  };

  const dialogTitle = selectedAddress?.reference_number
    ? intl.formatMessage({
        id: "settings.address-book.addressDetails",
        defaultMessage: "Address Details",
      })
    : intl.formatMessage({
        id: "settings.address-book.newAddress",
        defaultMessage: "New Address",
      });

  return (
    <Dialog show={isOpen} size="md" onHide={onClose} title={dialogTitle} isLoading={false}>
      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <AddressV2Form preferredContact={selectedAddress?.preferred_contact || null} />
          {!showConfirmDelete && (
            <div className="d-flex justify-content-between mt-5">
              <Button
                label={intl.formatMessage({
                  id: "general.cancel",
                  defaultMessage: "Cancel",
                })}
                variant="outline-primary"
                onClick={onClose}
              />
              <div className="d-flex">
                {selectedAddress && !forceCreate && (
                  <Button
                    className="mr-2"
                    label={intl.formatMessage({
                      id: "settings.address-book.deleteAddress",
                      defaultMessage: "Delete address",
                    })}
                    variant="danger"
                    onClick={() => setShowConfirmDelete(true)}
                  />
                )}
                <Button
                  label={intl.formatMessage({
                    id: "general.confirm",
                    defaultMessage: "Confirm",
                  })}
                  variant="success"
                  type="button"
                  onClick={handleSubmit(onSubmit)}
                  disabled={isLoadingMutate}
                />
              </div>
            </div>
          )}
          {showConfirmDelete && (
            <div className="d-flex justify-content-between mt-5">
              <Alert variant="warning">
                <FormattedMessage
                  id="settings.address-book.deleteAddressConfirm"
                  defaultMessage="Are you sure you want to delete this address?"
                />
              </Alert>
              <div className="d-flex">
                <Button
                  className="btn-grey ml-3"
                  label={intl.formatMessage({
                    id: "general.cancel",
                    defaultMessage: "Cancel",
                  })}
                  onClick={() => setShowConfirmDelete(false)}
                />
                <Button
                  className="ml-2"
                  label={intl.formatMessage({
                    id: "general.delete",
                    defaultMessage: "Delete",
                  })}
                  variant="danger"
                  isLoading={isDeleting}
                  onClick={handleDeleteAddress}
                />
              </div>
            </div>
          )}
        </form>
      </FormProvider>
    </Dialog>
  );
};

AddressBookDialog.defaultProps = {
  selectedAddress: undefined,
  onAddNewAddress: undefined,
  forceCreate: false,
  formSelectedShipperLocation: null,
};

export default AddressBookDialog;
